import { RejectedBannerIcon } from '@/assets/icons/info-icons/rejectedBanner';
import { Banner } from '@/components/basicComponents/banner';
import { FormFooter } from '@/components/basicComponents/formFooter';
import ModalLoader from '@/components/basicComponents/modal/modalLoader';
import ModalWrappedContent from '@/components/basicComponents/modal/modalWrappedContent';
import Textarea from '@/components/basicComponents/textarea';
import Header from '@/components/header';
import { GoBackButton } from '@/components/header/goBackButton';
import { PageTitle } from '@/components/header/pageTitle';
import { SettingsContainer, SettingsSection } from '@/components/settings-page';
import { useErrorMessages } from '@/hooks/i18n';
import { useResponsive } from '@/hooks/use-responsive';
import { MainWrap } from '@/styles/common';
import { themeVar } from '@/styles/theme';
import { useEffect, useState } from 'react';
import { IFirmSettings, IFontStyle } from '../types';

interface EditDisclosureTextProps {
  currentRow: IFirmSettings | null;
  saveSettingValue: (key: string, value: string | IFontStyle) => Promise<void>;
  handleClose: () => void;
}

export const EditDisclosureText = ({ currentRow, saveSettingValue, handleClose }: EditDisclosureTextProps) => {
  const { isMobile } = useResponsive();
  const { default: defaultErrorMessage } = useErrorMessages();

  const [disclosureTextValue, setDisclosureTextValue] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (currentRow?.value) {
      setDisclosureTextValue(currentRow.value);
    }
    return () => {
      setDisclosureTextValue('');
    };
  }, [currentRow]);

  const saveValue = async () => {
    try {
      setErrorMessage(null);
      setLoading(true);

      await saveSettingValue(currentRow?.key, disclosureTextValue);

      handleClose();
    } catch (error) {
      setErrorMessage(defaultErrorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {loading && (
        <ModalWrappedContent
          isOpen={loading}
          title="Saving"
          showCancelButton={false}
          showCloseIcon={false}
          isUseOutsideClick={false}
          onClose={() => {}}
        >
          <ModalLoader />
        </ModalWrappedContent>
      )}
      <MainWrap>
        <Header modalControl={<GoBackButton handleClose={handleClose} backToTitle="Templates" />} />
        <PageTitle title={currentRow?.fieldName ?? ''} />
      </MainWrap>
      <MainWrap>
        {errorMessage && (
          <Banner
            icon={<RejectedBannerIcon fill={themeVar('context.light')} />}
            title="Error"
            description={errorMessage}
            bgColor={themeVar('context.error')}
          />
        )}
        <SettingsContainer>
          <SettingsSection isMobile={isMobile}>
            <Textarea value={disclosureTextValue} onChange={(e) => setDisclosureTextValue(e.target.value)} size="lg" autoFocus />
          </SettingsSection>
          <FormFooter onCancel={handleClose} onSave={saveValue} disableSaveButton={loading} showRequiredFieldsWarning={false} />
        </SettingsContainer>
      </MainWrap>
    </>
  );
};
